import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'scratch-vm';

import Modal from '../../containers/modal.jsx';
import {getUser, setUser} from '../../lib/http/auth';
import {API_DOMAIN, http} from '../../lib/http/request';
import {
    USER_CENTER_DEFAULT_TAB,
    USER_CENTER_MATERIALS_TAB,
    USER_CENTER_PROFILE_TAB,
    USER_CENTER_PROJECTS_TAB,
    normalizeUserCenterTab
} from '../../lib/user-center-tabs';

import MaterialsPage from './materials-page.jsx';
import ProfilePage from './profile-page.jsx';
import ProjectsPage from './projects-page.jsx';

import styles from './user-center.css';

const MAX_AVATAR_SIZE = 2 * 1024 * 1024;
const API_ORIGIN = API_DOMAIN.replace(/\/api\/?$/, '');

const TAB_ITEMS = [
    {
        id: USER_CENTER_PROFILE_TAB,
        label: '我的资料'
    },
    {
        id: USER_CENTER_PROJECTS_TAB,
        label: '我的作品'
    },
    {
        id: USER_CENTER_MATERIALS_TAB,
        label: '我的素材'
    }
];

const DEFAULT_PROFILE = {
    nickname: '',
    name: '',
    avatar: '',
    sex: '',
    age: '',
    description: ''
};

const getCachedUser = () => {
    try {
        return getUser() || {};
    } catch (e) {
        return {};
    }
};

const normalizeProfile = user => {
    const safeUser = user || {};
    return {
        nickname: safeUser.nickname || safeUser.username || safeUser.account || DEFAULT_PROFILE.nickname,
        name: safeUser.name || DEFAULT_PROFILE.name,
        avatar: safeUser.avatar || safeUser.thumbnailUrl || DEFAULT_PROFILE.avatar,
        sex: safeUser.sex || safeUser.gender || DEFAULT_PROFILE.sex,
        age: safeUser.age ? String(safeUser.age) : DEFAULT_PROFILE.age,
        description: safeUser.description || safeUser.remark || DEFAULT_PROFILE.description
    };
};

const getAvatarSrc = avatar => {
    if (!avatar) return '';
    if (/^(data:|blob:|https?:)/.test(avatar)) return avatar;
    if (avatar.charAt(0) === '/') return `${API_ORIGIN}${avatar}`;
    return avatar;
};

const validateProfile = profile => {
    const nickname = profile.nickname.trim();
    const age = profile.age.trim();

    if (!nickname) return '请先填写用户昵称';
    if (age) {
        const ageNumber = Number(age);
        if (!Number.isInteger(ageNumber) || ageNumber < 1 || ageNumber > 120) {
            return '年龄需要填写 1 到 120 之间的数字';
        }
    }
    return '';
};

const getTabState = tab => {
    const normalizedTab = normalizeUserCenterTab(tab);
    return {
        activeTab: normalizedTab
    };
};

class UserCenterComponent extends React.Component {
    static getDerivedStateFromProps (props, state) {
        if (props.initialTab === state.lastInitialTab) {
            return null;
        }
        return Object.assign(getTabState(props.initialTab), {
            lastInitialTab: props.initialTab,
            saveStatus: '',
            saveStatusType: 'info'
        });
    }
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleAvatarChange',
            'handleAvatarPick',
            'handleChange',
            'handleClose',
            'handleGenderChange',
            'handleReset',
            'handleSubmit',
            'loadProfile',
            'renderActivePage',
            'setAvatarInput',
            'uploadAvatar'
        ]);
        const cachedProfile = normalizeProfile(getCachedUser());
        const tabState = getTabState(props.initialTab);
        this.state = {
            activeTab: tabState.activeTab,
            avatarFile: null,
            form: cachedProfile,
            loading: false,
            savedProfile: cachedProfile,
            saving: false,
            saveStatus: '',
            saveStatusType: 'info',
            lastInitialTab: props.initialTab
        };
        this.avatarInput = null;
        this.avatarPreviewUrl = null;
        this.mounted = false;
    }
    componentDidMount () {
        this.mounted = true;
        this.loadProfile();
    }
    componentWillUnmount () {
        this.mounted = false;
        this.releaseAvatarPreview();
    }
    setAvatarInput (input) {
        this.avatarInput = input;
    }
    loadProfile () {
        const cachedProfile = normalizeProfile(getCachedUser());
        this.setState({
            form: cachedProfile,
            loading: true,
            savedProfile: cachedProfile
        });
        http.get('/user/info')
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success && response.result) {
                    const profile = normalizeProfile(Object.assign({}, cachedProfile, response.result));
                    this.setState({
                        form: profile,
                        savedProfile: profile,
                        saveStatus: '',
                        saveStatusType: 'info'
                    });
                }
            })
            .catch(() => {
                if (!this.mounted) return;
                this.setState({
                    saveStatus: '已显示本地资料，联网后可继续同步',
                    saveStatusType: 'info'
                });
            })
            .then(() => {
                if (!this.mounted) return;
                this.setState({loading: false});
            });
    }
    releaseAvatarPreview () {
        if (this.avatarPreviewUrl && window.URL && window.URL.revokeObjectURL) {
            window.URL.revokeObjectURL(this.avatarPreviewUrl);
        }
        this.avatarPreviewUrl = null;
    }
    handleClose () {
        this.props.onRequestClose();
    }
    handleChange (event) {
        const field = event.currentTarget.getAttribute('data-field');
        const value = event.currentTarget.value;
        this.setState(prevState => ({
            form: Object.assign({}, prevState.form, {
                [field]: value
            }),
            saveStatus: '',
            saveStatusType: 'info'
        }));
    }
    handleGenderChange (event) {
        const value = event.currentTarget.value;
        this.setState(prevState => ({
            form: Object.assign({}, prevState.form, {
                sex: value
            }),
            saveStatus: '',
            saveStatusType: 'info'
        }));
    }
    handleAvatarPick () {
        if (this.avatarInput) {
            this.avatarInput.click();
        }
    }
    handleAvatarChange (event) {
        const file = event.currentTarget.files && event.currentTarget.files[0];
        if (!file) return;
        if (!/^image\//.test(file.type)) {
            this.setState({
                saveStatus: '请选择图片格式的头像',
                saveStatusType: 'error'
            });
            return;
        }
        if (file.size > MAX_AVATAR_SIZE) {
            this.setState({
                saveStatus: '头像图片不能超过 2MB',
                saveStatusType: 'error'
            });
            return;
        }

        this.releaseAvatarPreview();
        this.avatarPreviewUrl = window.URL.createObjectURL(file);
        this.setState(prevState => ({
            avatarFile: file,
            form: Object.assign({}, prevState.form, {
                avatar: this.avatarPreviewUrl
            }),
            saveStatus: '头像已选择，保存后生效',
            saveStatusType: 'info'
        }));
        event.currentTarget.value = '';
    }
    uploadAvatar () {
        if (!this.state.avatarFile) {
            return Promise.resolve(this.state.form.avatar);
        }

        const formData = new FormData();
        formData.append('file', this.state.avatarFile);
        formData.append('fileName', this.state.avatarFile.name);

        return http.post('/upload', formData)
            .then(response => {
                if (response && response.success && response.result) {
                    return response.result.url || response.result.filePath || this.state.form.avatar;
                }
                throw new Error((response && response.message) || '头像上传失败');
            });
    }
    handleSubmit (event) {
        event.preventDefault();
        const validationMessage = validateProfile(this.state.form);

        if (validationMessage) {
            this.setState({
                saveStatus: validationMessage,
                saveStatusType: 'error'
            });
            return;
        }

        this.setState({
            saving: true,
            saveStatus: '正在保存资料...',
            saveStatusType: 'info'
        });

        this.uploadAvatar()
            .then(avatar => {
                const profile = {
                    nickname: this.state.form.nickname.trim(),
                    name: this.state.form.name.trim(),
                    avatar,
                    sex: this.state.form.sex,
                    age: this.state.form.age.trim(),
                    description: this.state.form.description.trim()
                };

                return http.post('/user/updateInfo', {
                    nickname: profile.nickname,
                    name: profile.name,
                    avatar: profile.avatar,
                    sex: profile.sex,
                    age: profile.age ? Number(profile.age) : null,
                    description: profile.description
                }).then(response => ({
                    profile,
                    response
                }));
            })
            .then(({profile, response}) => {
                if (response && response.success === false) {
                    throw new Error(response.message || '资料保存失败');
                }

                setUser(Object.assign({}, getCachedUser(), profile));
                this.releaseAvatarPreview();
                if (!this.mounted) return;
                this.setState({
                    avatarFile: null,
                    form: profile,
                    savedProfile: profile,
                    saving: false,
                    saveStatus: '资料已保存',
                    saveStatusType: 'success'
                });
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    saving: false,
                    saveStatus: error.message || '资料保存失败，请稍后再试',
                    saveStatusType: 'error'
                });
            });
    }
    handleReset () {
        this.releaseAvatarPreview();
        this.setState(prevState => ({
            avatarFile: null,
            form: prevState.savedProfile,
            saveStatus: '',
            saveStatusType: 'info'
        }));
    }
    renderActivePage () {
        if (this.state.activeTab === USER_CENTER_PROFILE_TAB) {
            return (
                <ProfilePage
                    form={this.state.form}
                    getAvatarSrc={getAvatarSrc}
                    saveStatus={this.state.saveStatus}
                    saveStatusType={this.state.saveStatusType}
                    saving={this.state.saving}
                    setAvatarInput={this.setAvatarInput}
                    onAvatarChange={this.handleAvatarChange}
                    onAvatarPick={this.handleAvatarPick}
                    onChange={this.handleChange}
                    onGenderChange={this.handleGenderChange}
                    onReset={this.handleReset}
                    onSubmit={this.handleSubmit}
                />
            );
        }
        if (this.state.activeTab === USER_CENTER_PROJECTS_TAB) {
            return (
                <ProjectsPage onRequestClose={this.props.onRequestClose} />
            );
        }
        if (this.state.activeTab === USER_CENTER_MATERIALS_TAB) {
            return (
                <MaterialsPage vm={this.props.vm} />
            );
        }
        return null;
    }
    render () {
        const activeTab = TAB_ITEMS.find(item => item.id === this.state.activeTab) || TAB_ITEMS[0];

        return (
            <Modal
                fullScreen
                contentLabel={this.props.title}
                id={this.props.id}
                onRequestClose={this.handleClose}
            >
                <div className={styles.userCenter}>
                    <main className={styles.contentPanel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>{activeTab.label}</div>
                                <div className={styles.panelSubtitle}>
                                    {'管理你的 Scratch 创作身份'}
                                </div>
                            </div>
                            {this.state.loading ? (
                                <div className={styles.loadingPill}>{'读取资料中'}</div>
                            ) : null}
                        </div>
                        {this.renderActivePage()}
                    </main>
                </div>
            </Modal>
        );
    }
}

UserCenterComponent.propTypes = {
    id: PropTypes.string,
    initialTab: PropTypes.string,
    onRequestClose: PropTypes.func,
    title: PropTypes.string,
    vm: PropTypes.instanceOf(VM).isRequired
};

UserCenterComponent.defaultProps = {
    id: 'user-center',
    initialTab: USER_CENTER_DEFAULT_TAB,
    onRequestClose: () => {},
    title: '用户中心'
};

export default UserCenterComponent;
